import React, { useState } from 'react';
import { Shield, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../utils/authcontextt';
import { Button, Alert, Card, Input } from '../ui';
import MFASetup from '../auth/MFASetup';
import { buildApiUrl } from '../../config/api';

export default function SecuritySettings() {
  const { user, changePassword } = useAuth();
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isDisabling, setIsDisabling] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleEnableMFA = () => {
    setShowMFASetup(true);
    setAlert(null);
  };

  const handleMFAComplete = () => {
    setShowMFASetup(false);
    setAlert({
      type: 'success',
      message: 'Tvåfaktorsautentisering har aktiverats framgångsrikt!',
    });
    window.location.reload();
  };

  const handleDisableMFA = async () => {
    if (
      !window.confirm(
        'Är du säker på att du vill inaktivera tvåfaktorsautentisering? Detta kommer att minska säkerheten för ditt konto.',
      )
    ) {
      return;
    }

    setIsDisabling(true);
    setAlert(null);

    try {
      const response = await fetch(buildApiUrl('/auth/mfa/disable'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to disable MFA');
      }

      setAlert({
        type: 'success',
        message: 'Tvåfaktorsautentisering har inaktiverats.',
      });

      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setAlert({
        type: 'error',
        message:
          err instanceof Error ? err.message : 'Kunde inte inaktivera tvåfaktorsautentisering.',
      });
    } finally {
      setIsDisabling(false);
    }
  };

  const handlePasswordFieldChange =
    (field: keyof typeof passwordForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setPasswordForm((prev) => ({ ...prev, [field]: value }));
    };

  const validateNewPassword = (password: string) => {
    if (!password || password.length < 8) {
      return 'Lösenordet måste bestå av minst 8 tecken.';
    }

    if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
      return 'Använd minst en versal, en gemen och en siffra i ditt nya lösenord.';
    }

    return null;
  };

  const handlePasswordUpdate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAlert(null);

    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword) {
      setAlert({
        type: 'error',
        message: 'Nuvarande lösenord krävs för att byta till ett nytt.',
      });
      return;
    }

    const validationMessage = validateNewPassword(newPassword);
    if (validationMessage) {
      setAlert({ type: 'error', message: validationMessage });
      return;
    }

    if (newPassword !== confirmPassword) {
      setAlert({
        type: 'error',
        message: 'Det nya lösenordet och bekräftelsen matchar inte.',
      });
      return;
    }

    try {
      setIsUpdatingPassword(true);
      const result = await changePassword({ currentPassword, newPassword });

      if (!result.success) {
        setAlert({
          type: 'error',
          message: result.error || 'Kunde inte uppdatera lösenordet.',
        });
        return;
      }

      setAlert({
        type: 'success',
        message: 'Ditt lösenord har uppdaterats.',
      });
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'Ett oväntat fel uppstod när lösenordet skulle uppdateras.',
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (showMFASetup) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <MFASetup onComplete={handleMFAComplete} onCancel={() => setShowMFASetup(false)} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-brand-blue mb-2">Säkerhetsinställningar</h1>
        <p className="text-brand-blue/70">Hantera säkerhetsfunktioner och skydd för ditt konto</p>
      </div>

      {alert && <Alert type={alert.type} message={alert.message} />}

      <Card>
        <div className="p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-gradient-to-br from-brand-blue to-brand-secondary-blue rounded-lg">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-brand-blue mb-2">Kontosäkerhet</h2>
              <p className="text-brand-blue/70 text-sm">Din säkerhetsstatus och rekommendationer</p>
            </div>
          </div>

          <div className="grid gap-4">
            <div className="flex items-center gap-4 p-4 bg-brand-beige-tint-1/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-medium text-brand-blue">Lösenord</h3>
                <p className="text-sm text-brand-blue/70">Ditt lösenord är aktivt och säkert</p>
              </div>
            </div>

            <div
              className={`flex items-center gap-4 p-4 rounded-lg ${
                user?.mfa_enabled
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-amber-50 border border-amber-200'
              }`}
            >
              {user?.mfa_enabled ? (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              )}
              <div className="flex-1">
                <h3 className="font-medium text-brand-blue">Tvåfaktorsautentisering (2FA)</h3>
                <p className="text-sm text-brand-blue/70">
                  {user?.mfa_enabled
                    ? '2FA är aktiverad och skyddar ditt konto'
                    : '2FA är inte aktiverad - rekommenderas starkt för administratörer'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-gradient-to-br from-brand-blue to-brand-secondary-blue rounded-lg">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-brand-blue mb-2">Byt lösenord</h2>
              <p className="text-brand-blue/70 text-sm">
                Uppdatera ditt administratörslösenord för att hålla kontot säkert. Du behöver ange
                ditt nuvarande lösenord och ett nytt som uppfyller kraven nedan.
              </p>
            </div>
          </div>

          <form className="space-y-4" onSubmit={handlePasswordUpdate}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Nuvarande lösenord"
                type="password"
                required
                placeholder="••••••••••••"
                value={passwordForm.currentPassword}
                onChange={handlePasswordFieldChange('currentPassword')}
              />
              <Input
                label="Nytt lösenord"
                type="password"
                required
                placeholder="Minst 8 tecken"
                value={passwordForm.newPassword}
                onChange={handlePasswordFieldChange('newPassword')}
              />
              <div className="md:col-span-2">
                <Input
                  label="Bekräfta nytt lösenord"
                  type="password"
                  required
                  placeholder="Skriv igen för att bekräfta"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordFieldChange('confirmPassword')}
                />
              </div>
            </div>

            <div className="bg-brand-beige-tint-1/30 rounded-lg p-4 text-sm text-brand-blue/80">
              <p className="font-medium text-brand-blue mb-1">Lösenordskrav</p>
              <ul className="list-disc list-inside space-y-1 text-brand-blue/70">
                <li>Minst 8 tecken</li>
                <li>Minst en versal, en gemen och en siffra</li>
                <li>Rekommenderat: inkludera även ett specialtecken</li>
              </ul>
            </div>

            <div className="flex justify-end">
              <Button type="submit" variant="primary" disabled={isUpdatingPassword}>
                {isUpdatingPassword ? 'Uppdaterar...' : 'Uppdatera lösenord'}
              </Button>
            </div>
          </form>
        </div>
      </Card>

      <Card>
        <div className="p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-gradient-to-br from-brand-blue to-brand-secondary-blue rounded-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-brand-blue mb-2">
                Tvåfaktorsautentisering (2FA)
              </h2>
              <p className="text-brand-blue/70 text-sm mb-4">
                Lägg till ett extra lager av säkerhet till ditt konto genom att kräva både ditt
                lösenord och en verifieringskod från din mobiltelefon för att logga in.
              </p>
            </div>
          </div>

          <div className="bg-brand-beige-tint-1/30 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-brand-blue mb-3">Så fungerar det:</h3>
            <ol className="space-y-2 text-sm text-brand-blue/70">
              <li className="flex items-start gap-2">
                <span className="font-semibold text-brand-blue">1.</span>
                <span>
                  Ladda ner en autentiseringsapp (Google Authenticator, Authy, Microsoft
                  Authenticator)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-brand-blue">2.</span>
                <span>Skanna QR-koden med din app</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-brand-blue">3.</span>
                <span>Ange koden från appen varje gång du loggar in</span>
              </li>
            </ol>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-brand-blue/10">
            <div>
              <p className="font-medium text-brand-blue">
                Status:{' '}
                {user?.mfa_enabled ? (
                  <span className="text-green-600">Aktiverad</span>
                ) : (
                  <span className="text-amber-600">Inaktiverad</span>
                )}
              </p>
              {!user?.mfa_enabled && (
                <p className="text-sm text-brand-blue/70 mt-1">
                  Öka säkerheten genom att aktivera 2FA
                </p>
              )}
            </div>

            {user?.mfa_enabled ? (
              <Button
                variant="outline"
                onClick={handleDisableMFA}
                disabled={isDisabling}
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                {isDisabling ? (
                  <>
                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                    Inaktiverar...
                  </>
                ) : (
                  'Inaktivera 2FA'
                )}
              </Button>
            ) : (
              <Button variant="primary" onClick={handleEnableMFA}>
                Aktivera 2FA
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
